import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// CRUD for the user's diagnosed health conditions. Active rows are the
// working list; setting is_active = false moves a row to the resolved
// / archived history without losing it.
export function useHealthConditions({ includeInactive = false } = {}) {
  const { user } = useAuth()
  const [conditions, setConditions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    let query = supabase
      .from('health_conditions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
    if (!includeInactive) query = query.eq('is_active', true)
    const { data, error: err } = await query
    if (err) setError(err.message)
    else setConditions(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, includeInactive])

  useEffect(() => { fetch() }, [fetch])

  const create = async (values) => {
    if (!user) return null
    const { data, error: err } = await supabase
      .from('health_conditions')
      .insert({ ...values, user_id: user.id })
      .select('*')
      .single()
    if (err) {
      setError(err.message)
      return null
    }
    await fetch()
    return data
  }

  const update = async (id, patch) => {
    const { error: err } = await supabase
      .from('health_conditions')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  const remove = async (id) => {
    const { error: err } = await supabase.from('health_conditions').delete().eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  return { conditions, loading, error, create, update, remove, refetch: fetch }
}
